import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowUp, ChevronDown, ChevronUp, CircleCheck, MessageSquare } from 'lucide-react';
import { MarkdownContent } from '../ui/markdown-content';

export interface NormalTextResponseProps {
  /** Bot message text (normal_text from backend). */
  content: string;
  /** Suggested follow-up queries shown as buttons. */
  suggestedQueries?: string[];
  /** Called when user clicks a suggestion. */
  onSuggestedQueryClick?: (query: string) => void;
  /** Whether to show the feedback button (opens FeedbackModal in parent). */
  showFeedbackButton?: boolean;
  /** Called when user clicks feedback (parent opens FeedbackModal). */
  onFeedbackClick?: () => void;
  isNewEntry?: boolean;
  className?: string;
}

/**
 * Normal_text response: 1) Bot message, 2) Suggested queries, 3) Feedback button.
 * Parent (ChatInterface) owns FeedbackModal; this component only triggers it.
 */
export function NormalTextResponse({
  content,
  suggestedQueries = [],
  onSuggestedQueryClick,
  showFeedbackButton,
  onFeedbackClick,
  isNewEntry = false,
  className = '',
}: NormalTextResponseProps) {
  const [followUpsOpen, setFollowUpsOpen] = useState(suggestedQueries.length > 0);

  useEffect(() => {
    if (suggestedQueries.length > 0) setFollowUpsOpen(true);
  }, [suggestedQueries.length]);

  if (!content?.trim()) return null;

  return (
    // Repo-2 branding pass: align text-only assistant replies with the V2 chat skin.
    // Search "Repo-2 branding pass" to reuse or revert this styling layer later.
    <motion.div
      initial={isNewEntry ? { opacity: 0, y: 10 } : false}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, ease: 'easeOut' }}
      layout
      className={`mb-3 flex flex-col items-start ${className}`}
    >
      <div className="mb-1.5 flex items-center gap-1.5 text-[10.5px] text-[#7B6B72]">
        <CircleCheck className="h-3 w-3 text-[#3F7D4F]" />
        <span className="uppercase tracking-[0.18em]">Assistant response</span>
      </div>

      <div className="min-w-0 w-full max-w-[92%] rounded-[12px] border border-[#EDE6E1] bg-white px-3.5 py-3 text-[#1F1A20] shadow-[0_1px_0_rgba(31,26,32,.04),0_1px_3px_rgba(31,26,32,.05)]">
        <MarkdownContent content={content.trim()} className="text-[13px] font-normal leading-relaxed text-[#3F3338]" />

        {suggestedQueries.length > 0 && onSuggestedQueryClick && (
          <div className="mt-3 border-t border-[#EDE6E1] pt-2">
            <button
              type="button"
              onClick={() => setFollowUpsOpen((open) => !open)}
              className="flex w-full items-center justify-between text-[10.5px] text-[#7B6B72] transition-colors hover:text-[#1F1A20]"
              aria-expanded={followUpsOpen}
            >
              <span className="flex items-center gap-1.5">
                <ArrowUp className="h-3 w-3 rotate-45 text-[#6E3450]" />
                <span className="uppercase tracking-[0.18em]">Suggested follow-ups</span>
                <span className="text-[#A8989F]">· {suggestedQueries.length}</span>
              </span>
              {followUpsOpen ? <ChevronUp className="h-3 w-3" /> : <ChevronDown className="h-3 w-3" />}
            </button>

            {followUpsOpen && (
              <div className="mt-2 grid gap-1.5">
              {suggestedQueries.map((suggestion, idx) => (
                <button
                  key={idx}
                  type="button"
                  onClick={() => onSuggestedQueryClick(suggestion)}
                  className="w-full rounded-[8px] border border-[#EDE5D8] bg-white px-2.5 py-2 text-left text-[12px] leading-snug text-[#3F3338] transition-colors hover:border-[#6E3450]/40 hover:bg-[#f5e9ee]/60 hover:text-[#1F1A20]"
                  title="Click to ask this question with the same data source"
                >
                  {suggestion}
                </button>
              ))}
              </div>
            )}
          </div>
        )}

      </div>

      {/* Feedback opens FeedbackModal in the parent. */}
      {showFeedbackButton && onFeedbackClick && (
        <button
          type="button"
          onClick={onFeedbackClick}
          className="mt-2 inline-flex items-center gap-1.5 rounded-[8px] border border-[#6E3450]/25 bg-white px-2.5 py-1.5 text-[11px] font-medium text-[#6E3450] transition-colors hover:bg-[#f5e9ee]/60"
          title="Provide feedback"
        >
          <MessageSquare className="h-3.5 w-3.5" />
          Feedback
        </button>
      )}
    </motion.div>
  );
}
